import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import { ContentActionsService } from '~commonInfra/contentActions/ContentActions';
import { FeatureFlags } from '~commonInfra/featureFlags/FeatureFlags';
import { MapDispatchToProps, MapStateToProps } from '~commonInfra/utils/ConnectUtils';
import { Set } from '~commonInfra/utils/Set';
import { CompositeSessionInfoSelectors, SessionUserSelectors } from '~src/bootstrap/redux/selectors/SessionInfoSelectors';
import { siteUrlsSelector } from '~src/bootstrap/redux/selectors/SiteUrlsSelectors';
import { AppState } from '~src/bootstrap/redux/state/AppState';
import { getAppStore } from '~src/bootstrap/redux/store/AppStore';
import { copyToClipboard } from '~src/broadcasts/components/PublicFacingUrlCopyButton';
import { selectCatalogingOnSiteEnabled } from '~src/catalog/selectors/CatalogSelectors';
import { Messages } from '~src/codegen/Localize';
import { IDataSourceDetailInfoWithUsage } from '~src/datasource/models/IDataSourceInfo';
import { IDatasourceActions } from '~src/datasource/ServerApi/types/IDatasourceActions';
import { ChangeDatasourceDescriptionAction } from '~src/datasource/services/ChangeDatasourceDescriptionAction';
import { IResult } from '~src/httpClient/serverApi/types/IResponse';
import { ResultHelper } from '~src/httpClient/utils/ResultHelper';
import { RevisionHistoryServiceInstance } from '~src/revisionHistory/services/RevisionHistoryService';
import { incrementRouterSpaceReactKey } from '~src/spacesPlatform/actions/IncrementRouterSpaceReactKeyAction';
import {
  ModifiedAtDetailSection,
  ModifiedByDetailSection
} from '~src/spacesPlatform/components/content/contentDetailDialog/CommonContentDetailSections';
import { ContentDetailSection, DetailEditButton, sectionContainer } from '~src/spacesPlatform/components/content/contentDetailDialog/ContentDetailSection';
import { ConnectedDeleteContentSection } from '~src/spacesPlatform/components/content/contentDetailDialog/DeleteContentSection';
import {
  DESCRIPTION_MAX_LENGTH,
  DescriptionDetailSection
} from '~src/spacesPlatform/components/content/contentDetailDialog/DescriptionDetailSection';
import { ConnectedLocationDetailSection } from '~src/spacesPlatform/components/content/contentDetailDialog/LocationDetailSection';
import { ConnectedOwnerDetailSection } from '~src/spacesPlatform/components/content/contentDetailDialog/OwnerDetailSection';
import { TagsDetailSection } from '~src/spacesPlatform/components/content/contentDetailDialog/tags/TagsDetailSection';
import { SpacesSiteUrls } from '~src/spacesPlatform/components/spaceComponents/SpacesSiteUrls';
import { ChangeTagsActionHelperService } from '~src/tags/ChangeTagsActionHelper';
import { TagsHelper } from '~src/tags/TagsInputHelper';

import { ChangeDatasourceCertificationAction } from '../services/ChangeDatasourceCertificationAction';
import { DataCertificationSection } from './DataCertificationSection';
import { RemoteQueryAgentDetailSection } from './RemoteQueryAgentDetailSection';


interface DatasourceDetailsDialogBodyOwnProps {
  readonly datasource: IDataSourceDetailInfoWithUsage;
  readonly allowedActions: IDatasourceActions;
  readonly location: string;
}

interface DatasourceDetailsDialogBodyStateProps {
  readonly isAdminOrOwner: boolean;
  readonly urls: SpacesSiteUrls;
  readonly isCatalogEnabled: boolean;
}

interface DatasourceDetailsDialogBodyDispatchProps {
  readonly incrementRouterSpaceReactKey: () => void;
}

export type DatasourceDetailsDialogBodyProps =
    DatasourceDetailsDialogBodyOwnProps &
    DatasourceDetailsDialogBodyStateProps &
    DatasourceDetailsDialogBodyDispatchProps;

export function DatasourceDetailsDialogBody(props: DatasourceDetailsDialogBodyProps): JSX.Element {
  const { datasource, allowedActions, isAdminOrOwner, urls, location, incrementRouterSpaceReactKey, isCatalogEnabled } = props;

  function allowedAction(actionName: keyof IDatasourceActions): boolean {
    return !!(allowedActions?.[actionName]);
  }

  const setDescription = React.useCallback((description: string) => {
    return ChangeDatasourceDescriptionAction.execute(datasource, description);
  }, [datasource]);

  const moveToProject = React.useCallback(() => {
    return ContentActionsService.moveDatasourcesToProject(Set.singletonForIdItem(datasource));
  }, [datasource]);

  const setTags = React.useCallback((tags: string[]) => {
    return ChangeTagsActionHelperService.changeTagsForContent([datasource], TagsHelper.convertArrayToMap(datasource.tags), TagsHelper.convertArrayToMap(tags))
        .then((results: IResult[]) => ResultHelper.combineResults(results));
  }, [datasource]);

  const showRevisionHistory = React.useCallback(() => {
    return RevisionHistoryServiceInstance.showDataSourceRevisionHistory(datasource);
  }, [datasource]);

  const setCertification = React.useCallback((isCertified: boolean, certificationNote: string) => {
    return ChangeDatasourceCertificationAction.execute(datasource, { isCertified, certificationNote });
  }, [datasource]);

  const allowRemoteQueryAgentDetailSection =
    FeatureFlags.bridgeLiveDirectAssignEnabled &&
    !props.datasource.hasExtracts &&
    !props.datasource.testingConnectionAllowed;

  const userId = SessionUserSelectors.sessionUserId(getAppStore().getState());
  const isOwner = datasource.owner? (userId === datasource.owner.id) : true;

  // convert tags into a map to be passed as prop to TagsDetailSection
  const tagMap = TagsHelper.convertArrayToMap(datasource.tags);
  return (
    <div className={sectionContainer}>
      <DescriptionDetailSection
        description={datasource.description}
        isActionEnabled={allowedAction('setDescription')}
        setDescription={setDescription}
        descriptionMaxLength={DESCRIPTION_MAX_LENGTH}
        showPreviewAndFormattingHints={true}
        incrementRouterSpaceReactKey={incrementRouterSpaceReactKey}
      />
      {
        <ConnectedLocationDetailSection
        location={location}
        project={datasource.project}
        isActionEnabled={allowedAction('setProject')}
        content={datasource}
        urls={urls}
        incrementRouterSpaceReactKey={incrementRouterSpaceReactKey}
        isOwner={isOwner}
      />
      }
      <ConnectedOwnerDetailSection content={datasource} isActionEnabled={allowedAction('setOwner')} urls={urls}/>
      <TagsDetailSection
        tags={tagMap}
        isActionEnabled={allowedAction('setTags')}
        setTags={setTags}
        tagUrlFn={(tag: string) => (urls.datasourcesWithTagUrl(tag))}
        incrementRouterSpaceReactKey={incrementRouterSpaceReactKey}
      />
      <ModifiedAtDetailSection
        date={datasource.updatedAt}
        allowShowRevisionHistory={allowedAction('versionManagement')}
        showRevisionHistory={showRevisionHistory}
      />
      {/*DEBUG SECTION ONLY*/
        allowRemoteQueryAgentDetailSection &&
          <RemoteQueryAgentDetailSection datasource={datasource} />
      }
      {!isCatalogEnabled &&
        <DataCertificationSection
          isCertified={datasource.certificationDetails.isCertified}
          certifier={datasource.certificationDetails.certifier}
          note={datasource.certificationDetails.certificationNote}
          isActionEnabled={allowedAction('setCertification')}
          urls={urls}
          setCertification={setCertification}
          incrementRouterSpaceReactKey={incrementRouterSpaceReactKey}
        />
      }
      {FeatureFlags.modifiedByUserEnabled &&
        <ModifiedByDetailSection user={datasource.modifiedBy} urls={urls} />
      }
      {
        props.datasource.luid &&
        <ContentDetailSection
          testId='luid-detail'
          header={Messages.luid()}
          detail={props.datasource.luid}
          isActionAllowed={true}
          action={<DetailEditButton
            buttonLabel={Messages.luidCopy()}
            buttonTestId={'copy-luid-button'}
            onClickAction={() => copyToClipboard(props.datasource.luid, Messages.luidCopied())}
          />}
        />
      }
      {allowedAction('del') &&
        <ConnectedDeleteContentSection
          actionMessage={Messages.deleteDataSource_action()}
          content={datasource}
        />
      }
    </div>
  );
}

const mapDispatchToProps: MapDispatchToProps<DatasourceDetailsDialogBodyDispatchProps> = (dispatch) => bindActionCreators({
  incrementRouterSpaceReactKey: incrementRouterSpaceReactKey
}, dispatch);

const mapStateToProps: MapStateToProps<DatasourceDetailsDialogBodyStateProps> = (state: AppState, ownProps: DatasourceDetailsDialogBodyOwnProps) => ({
  isAdminOrOwner: CompositeSessionInfoSelectors.currentUserIsAdminOrOwnerSelector(ownProps.datasource.owner)(state),
  urls: siteUrlsSelector(state),
  isCatalogEnabled: selectCatalogingOnSiteEnabled(state),
});

export const ConnectedDatasourceDetailsDialogBody: React.ComponentClass<DatasourceDetailsDialogBodyOwnProps> =
  connect(mapStateToProps, mapDispatchToProps)(DatasourceDetailsDialogBody);


